export type RigRecordKind = 'nodeInstance' | 'panel' | 'scene' | 'output' | 'input';

export interface RigRecord {
  kind: RigRecordKind;
  segments: string[];
  revision: number;
  state: Record<string, any>;
  units: Record<string, { revision: number }>;
  updatedAt?: string;
  deletedAt?: string;
}

export interface RecordSet { path: string; value: unknown }
export interface RecordWriteReply {
  revision: number;
  units: Array<{ unitKey: string; outcome: 'applied' | 'duplicate' | 'refused'; reason?: string }>;
  applied?: number;
}

/** Refusals named by K1 and by the dispatch freeze guard. Their messages are never operator copy. */
export const RECORD_REFUSAL_REASONS = [
  'no-controller', 'unknown-kind', 'unsupported-op', 'rate-above-profile', 'card-mismatch',
  'card-missing', 'record-deleted', 'profile-missing', 'room-entry-invalid',
  'record-identity-mismatch', 'effect-catalog-invalid', 'binding-changed', 'controller-unassigned', 'frozen',
] as const;

export function recordRefusalReason(error: unknown): typeof RECORD_REFUSAL_REASONS[number] | undefined {
  const details = error && typeof error === 'object' && 'details' in error ? error.details : undefined;
  const reason = details && typeof details === 'object' && 'reason' in details ? details.reason : undefined;
  return RECORD_REFUSAL_REASONS.find((candidate) => candidate === reason);
}

/** R0's byte encoding, shared by record ids and unit path segments. */
export function encodeRecordSegment(value: string): string {
  return [...new TextEncoder().encode(value)].map((byte) =>
    byte >= 65 && byte <= 90 || byte >= 97 && byte <= 122 || byte >= 48 && byte <= 57
      || byte === 95 || byte === 45
      ? String.fromCharCode(byte) : `%${byte.toString(16).toUpperCase().padStart(2, '0')}`,
  ).join('');
}

export function rigRecordId(kind: RigRecordKind, segments: string[] = []): string {
  const count = kind === 'nodeInstance' || kind === 'panel' || kind === 'scene' ? 1 : 0;
  if (segments.length !== count || segments.some((segment) => !segment)) {
    throw new Error(`Invalid ${kind} record identity.`);
  }
  return [kind, ...segments.map(encodeRecordSegment)].join(':');
}

export function recordUnitName(kind: RigRecordKind, path: string): string {
  if (kind === 'scene' || kind === 'input') return 'record';
  if (kind === 'nodeInstance' && /^layers\/[^/]+(?:\/index)?$/.test(path)) return 'layers';
  if (kind === 'nodeInstance' && /^layers\/[^/]+\/bindings\/[^/]+$/.test(path)) return path;
  return path;
}

export function recordBase(
  recordId: string, kind: RigRecordKind, current: RigRecord | undefined,
  op: 'set' | 'replace' | 'delete', writes: readonly RecordSet[] = [],
): Record<string, number> {
  const names = op === 'set' ? writes.map((write) => recordUnitName(kind, write.path)) : ['record'];
  return Object.fromEntries([...new Set(names)].map((name) => {
    const key = `${recordId}|${name}`;
    return [key, current?.units?.[key]?.revision ?? 0];
  }));
}

export function newRecordOpId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return `${Date.now().toString(36)}-${[...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/** Stable instance identity for a card newly added to a rig. Existing migrated ids are retained. */
export async function newNodeInstanceId(rigId: string, packageId: string): Promise<string> {
  const namespace = new Uint8Array([0x6b, 0xa7, 0xb8, 0x11, 0x9d, 0xad, 0x11, 0xd1,
    0x80, 0xb4, 0x00, 0xc0, 0x4f, 0xd4, 0x30, 0xc8]);
  const name = new TextEncoder().encode(`${rigId}:${packageId}`);
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-1', new Uint8Array([...namespace, ...name]))).slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function applyRecordSets(state: Record<string, any>, writes: readonly RecordSet[]): Record<string, any> {
  const next = structuredClone(state);
  for (const { path, value } of writes) {
    const segments = path.split('/').map(decodeURIComponent);
    let parent: Record<string, any> = next;
    for (const segment of segments.slice(0, -1)) {
      if (parent[segment] == null || typeof parent[segment] !== 'object') parent[segment] = {};
      parent = parent[segment];
    }
    parent[segments.at(-1)!] = value;
  }
  return next;
}

/** The old card editor's addresses become the K1 node-instance addresses. */
export function effectRecordSets(writes: readonly RecordSet[]): RecordSet[] {
  return writes.map(({ path, value }) => {
    if (path.startsWith('parameters/')) return { path: `bindings/${encodeRecordSegment(path.slice(11))}`, value };
    if (path === 'sync/musicRate') return { path: 'bindings/musicRate', value };
    if (path === 'mix') return { path: 'bindings/mix', value };
    if (path.startsWith('layers/')) return { path, value };
    throw new Error('The controller refused that setting.');
  });
}
